import React from 'react';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';

/**
 * Generates a standard "Actions" column config for DataGridPro tables.
 * Renders Edit/Delete buttons in view mode and Save/Cancel buttons in edit mode.
 *
 * Used by HoldTable, SupplierTable, MarketTable, and ShipTable.
 * CarrierSupplierMap has a custom actions column (Copy + conditional Delete)
 * so it defines its own inline instead of using this utility.
 *
 * @param {Object}   options
 * @param {Object}   options.rowModesModel    - Current row modes from useRowEditing
 * @param {Function} options.handleEditClick  - From useRowEditing
 * @param {Function} options.handleSaveClick  - From useRowEditing
 * @param {Function} options.handleCancelClick- From useRowEditing
 * @param {Function} options.handleDeleteClick- Table-specific delete handler
 * @param {number}   [options.width=100]      - Column width
 */
const getActionsColumn = ({
  rowModesModel,
  handleEditClick,
  handleSaveClick,
  handleCancelClick,
  handleDeleteClick,
  width = 100,
}) => ({
  field: 'actions',
  type: 'actions',
  headerName: 'Actions',
  width,
  cellClassName: 'actions',
  getActions: ({ id }) => {
    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
    if (isInEditMode) {
      return [
        <GridActionsCellItem
          key='save'
          icon={<SaveIcon />}
          label='Save'
          sx={{ color: 'primary.main' }}
          onClick={handleSaveClick(id)}
        />,
        <GridActionsCellItem
          key='cancel'
          icon={<CancelIcon />}
          label='Cancel'
          className='textPrimary'
          onClick={handleCancelClick(id)}
          color='inherit'
        />,
      ];
    }
    return [
      <GridActionsCellItem
        key='edit'
        icon={<EditIcon />}
        label='Edit'
        className='textPrimary'
        onClick={handleEditClick(id)}
        color='inherit'
      />,
      <GridActionsCellItem
        key='delete'
        icon={<DeleteIcon />}
        label='Delete'
        onClick={handleDeleteClick(id)}
        color='inherit'
      />,
    ];
  },
});

export default getActionsColumn;
